import classNames from "classnames";
import { NetworkStatus, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useCapabilities, { useCanViewCareerServicesDashboardBool } from "@/hooks/useCapabilities";
import {
  MEMBER_DASHBOARD_ACTIVITY_QUERY,
  MEMBER_DASHBOARD_CELEBRATIONS_QUERY,
  MEMBER_DASHBOARD_PACING_QUERY,
  MEMBER_DASHBOARD_SIGNALS_QUERY,
  type MemberDashboardActivityQueryVariables,
  type MemberDashboardCelebrationsQueryVariables,
  type MemberDashboardPacingQueryVariables,
  type MemberDashboardSignalsQueryVariables
} from "../queries";
import {
  toDashboardMemberActivityGroups,
  toDashboardMemberCelebrationGroups,
  toDashboardMemberSignalGroups
} from "../_utils/memberDashboardSignalDisplay";
import { toDashboardMemberPacingRows } from "../_utils/memberDashboardPacingDisplay";
import { isInitialQueryLoading } from "../_utils/isInitialQueryLoading";
import DashboardPacingPanel from "./DashboardPacingPanel";
import DashboardSignalTable from "./DashboardSignalTable";
import CareerServicesDashboardGate from "./CareerServicesDashboardGate";
import DashboardTimeframeToggle from "./DashboardTimeframeToggle";
import { DASHBOARD_BOTTOM_SCROLL_PADDING_CLASS } from "../_utils/dashboardPanelStyles";
import { DEFAULT_DASHBOARD_WINDOW } from "../_utils/dashboardTimeframe";

const MEMBER_DASHBOARD_PREVIEW_LIMIT = 3;

function MembersDashboardContent() {
  const [searchParams] = useSearchParams();
  const [dashboardWindow, setDashboardWindow] = useState(DEFAULT_DASHBOARD_WINDOW);
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canViewCareerServicesDashboard = useCanViewCareerServicesDashboardBool();
  const sectionId = searchParams.get("sectionId");
  const signalVariables: MemberDashboardSignalsQueryVariables = {
    input: {
      window: dashboardWindow,
      previewLimit: MEMBER_DASHBOARD_PREVIEW_LIMIT,
      ...(sectionId ? { sectionId } : {})
    }
  };
  const activityVariables: MemberDashboardActivityQueryVariables = {
    input: {
      window: dashboardWindow,
      previewLimit: MEMBER_DASHBOARD_PREVIEW_LIMIT,
      ...(sectionId ? { sectionId } : {})
    }
  };
  const celebrationVariables: MemberDashboardCelebrationsQueryVariables = {
    input: {
      window: dashboardWindow,
      previewLimit: MEMBER_DASHBOARD_PREVIEW_LIMIT,
      ...(sectionId ? { sectionId } : {})
    }
  };
  const pacingVariables: MemberDashboardPacingQueryVariables = {
    input: {
      kinds: ["PDR", "DUES"],
      ...(sectionId ? { sectionId } : {})
    }
  };
  const dashboardQueriesEnabled = capabilitiesLoaded && canViewCareerServicesDashboard;
  const { data, previousData, loading, networkStatus, error } = useQuery(MEMBER_DASHBOARD_SIGNALS_QUERY, {
    variables: signalVariables,
    skip: !dashboardQueriesEnabled,
    fetchPolicy: "cache-and-network"
  });
  const signalQuerySettled =
    dashboardQueriesEnabled && Boolean(error || (data && networkStatus === NetworkStatus.ready));
  const [activityQueryStarted, setActivityQueryStarted] = useState(false);
  const [celebrationQueryStarted, setCelebrationQueryStarted] = useState(false);
  const [pacingQueryStarted, setPacingQueryStarted] = useState(false);

  useEffect(() => {
    if (!dashboardQueriesEnabled) {
      setActivityQueryStarted(false);
      setCelebrationQueryStarted(false);
      setPacingQueryStarted(false);
    }
  }, [dashboardQueriesEnabled]);

  useEffect(() => {
    if (signalQuerySettled) {
      setActivityQueryStarted(true);
    }
  }, [signalQuerySettled]);

  const {
    data: activityData,
    previousData: previousActivityData,
    loading: activityLoading,
    networkStatus: activityNetworkStatus,
    error: activityError
  } = useQuery(MEMBER_DASHBOARD_ACTIVITY_QUERY, {
    variables: activityVariables,
    skip: !dashboardQueriesEnabled || !activityQueryStarted,
    fetchPolicy: "cache-and-network"
  });
  const activityQuerySettled =
    signalQuerySettled && Boolean(activityError || (activityData && activityNetworkStatus === NetworkStatus.ready));
  useEffect(() => {
    if (activityQuerySettled) {
      setCelebrationQueryStarted(true);
    }
  }, [activityQuerySettled]);

  const {
    data: celebrationData,
    previousData: previousCelebrationData,
    loading: celebrationLoading,
    networkStatus: celebrationNetworkStatus,
    error: celebrationError
  } = useQuery(MEMBER_DASHBOARD_CELEBRATIONS_QUERY, {
    variables: celebrationVariables,
    skip: !dashboardQueriesEnabled || !celebrationQueryStarted,
    fetchPolicy: "cache-and-network"
  });
  const celebrationQuerySettled =
    activityQuerySettled &&
    Boolean(celebrationError || (celebrationData && celebrationNetworkStatus === NetworkStatus.ready));
  useEffect(() => {
    if (celebrationQuerySettled) {
      setPacingQueryStarted(true);
    }
  }, [celebrationQuerySettled]);

  const {
    data: pacingData,
    previousData: previousPacingData,
    loading: pacingLoading,
    networkStatus: pacingNetworkStatus,
    error: pacingError
  } = useQuery(MEMBER_DASHBOARD_PACING_QUERY, {
    variables: pacingVariables,
    skip: !dashboardQueriesEnabled || !pacingQueryStarted,
    fetchPolicy: "cache-and-network"
  });
  const dashboardSignals = data?.memberDashboardSignals ?? previousData?.memberDashboardSignals;
  const dashboardActivity = activityData?.memberDashboardActivity ?? previousActivityData?.memberDashboardActivity;
  const dashboardCelebrations =
    celebrationData?.memberDashboardCelebrations ?? previousCelebrationData?.memberDashboardCelebrations;
  const dashboardPacing = pacingData?.memberDashboardPacing ?? previousPacingData?.memberDashboardPacing;
  const memberSignalGroups = toDashboardMemberSignalGroups(dashboardSignals?.signals ?? [], dashboardWindow);
  const memberActivityGroups = toDashboardMemberActivityGroups(dashboardActivity?.activities ?? [], dashboardWindow);
  const memberCelebrationGroups = toDashboardMemberCelebrationGroups(dashboardCelebrations?.celebrations ?? []);
  const memberPacingRows = toDashboardMemberPacingRows(dashboardPacing?.metrics ?? []);
  const totalMemberRows = memberSignalGroups.reduce((total, group) => total + group.rows.length, 0);
  const totalMemberActivityRows = memberActivityGroups.reduce((total, group) => total + group.rows.length, 0);
  const totalMemberCelebrationRows = memberCelebrationGroups.reduce((total, group) => total + group.rows.length, 0);
  const dashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard &&
      isInitialQueryLoading({
        loading,
        networkStatus,
        itemCount: totalMemberRows,
        hasData: dashboardSignals !== undefined,
        error
      }));
  const activityDashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard &&
      (!activityQueryStarted ||
        isInitialQueryLoading({
          loading: activityLoading,
          networkStatus: activityNetworkStatus,
          itemCount: totalMemberActivityRows,
          hasData: dashboardActivity !== undefined,
          error: activityError
        })));
  const celebrationDashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard &&
      (!celebrationQueryStarted ||
        isInitialQueryLoading({
          loading: celebrationLoading,
          networkStatus: celebrationNetworkStatus,
          itemCount: totalMemberCelebrationRows,
          hasData: dashboardCelebrations !== undefined,
          error: celebrationError
        })));
  const pacingDashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard &&
      (!pacingQueryStarted ||
        isInitialQueryLoading({
          loading: pacingLoading,
          networkStatus: pacingNetworkStatus,
          itemCount: memberPacingRows.pdr.length + memberPacingRows.dues.length,
          hasData: dashboardPacing !== undefined,
          error: pacingError
        })));
  const dashboardUnavailable = capabilitiesLoaded && !canViewCareerServicesDashboard;
  const dashboardError = Boolean(error && !dashboardSignals);
  const activityDashboardError = Boolean(activityError && !dashboardActivity);
  const celebrationDashboardError = Boolean(celebrationError && !dashboardCelebrations);
  const pacingDashboardError = Boolean(pacingError && !dashboardPacing);

  return (
    <div
      className={classNames("mt-12 flex flex-col gap-6", DASHBOARD_BOTTOM_SCROLL_PADDING_CLASS)}
      data-testid="home-members-dashboard"
    >
      <div className="flex w-full justify-start" data-testid="home-members-timeframe-row">
        <DashboardTimeframeToggle
          value={dashboardWindow}
          onChange={setDashboardWindow}
          testId="home-members-timeframe"
        />
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          <DashboardSignalTable
            title="Needs Attention"
            icon="warning"
            testId="home-members-needs-attention"
            tableTestId="home-members-needs-attention-table"
            tableAriaLabel="Member needs attention"
            groupTestIdPrefix="home-members-needs-attention-group"
            rowTestIdPrefix="home-member-needs-attention-row"
            personLabel="member"
            groups={memberSignalGroups}
            loading={dashboardLoading}
            error={dashboardError}
            unavailable={dashboardUnavailable}
          />
          <DashboardSignalTable
            title="Recent Career & Membership Activity"
            icon="swap_horiz"
            testId="home-members-activity"
            tableTestId="home-members-activity-table"
            tableAriaLabel="Recent member career and membership activity"
            groupTestIdPrefix="home-members-activity-group"
            rowTestIdPrefix="home-member-activity-row"
            personLabel="member"
            groups={memberActivityGroups}
            loading={activityDashboardLoading}
            error={activityDashboardError}
            unavailable={dashboardUnavailable}
          />
          <DashboardSignalTable
            title="Celebrations"
            icon="celebration"
            testId="home-members-celebrations"
            tableTestId="home-members-celebrations-table"
            tableAriaLabel="Member celebrations"
            groupTestIdPrefix="home-members-celebrations-group"
            rowTestIdPrefix="home-member-celebration-row"
            personLabel="member"
            groups={memberCelebrationGroups}
            loading={celebrationDashboardLoading}
            error={celebrationDashboardError}
            unavailable={dashboardUnavailable}
          />
        </div>

        <aside className="flex w-full flex-col gap-4" data-testid="home-members-pacing">
          <DashboardPacingPanel
            title="PDR"
            progress={memberPacingRows.pdr}
            testId="home-members-pdr-pacing"
            audienceLabel="Member"
            loading={pacingDashboardLoading}
            error={pacingDashboardError}
            unavailable={dashboardUnavailable}
          />
          <DashboardPacingPanel
            title="Dues"
            progress={memberPacingRows.dues}
            testId="home-members-dues-pacing"
            audienceLabel="Member"
            loading={pacingDashboardLoading}
            error={pacingDashboardError}
            unavailable={dashboardUnavailable}
          />
        </aside>
      </div>
    </div>
  );
}

export default function MembersDashboard() {
  return (
    <CareerServicesDashboardGate>
      <MembersDashboardContent />
    </CareerServicesDashboardGate>
  );
}
